/**
 * Build-stable cache busting for runtime public assets (`/assets/...`).
 *
 * Root cause this solves: runtime PNG/JSON assets are served from stable URLs
 * that do not change when their content changes (they are replaced in place,
 * e.g. PixelLab skin regenerations). Browsers/edge caches then keep serving the
 * old bytes even after a new JS bundle deploys. Appending a per-deployment
 * `?v=<build version>` makes every asset replacement take effect on the next
 * page load with no manual hard refresh.
 *
 * The version is injected at build time via the `__GRAVELIGHT_ASSET_VERSION__`
 * define in `vite.config.ts` (Cloudflare Pages `CF_PAGES_COMMIT_SHA` →
 * `GIT_COMMIT_SHA` → local `git rev-parse` → `"dev"`). Logical asset paths and
 * appearance asset ids stay stable — only the URL actually fetched changes.
 */

declare const __GRAVELIGHT_ASSET_VERSION__: string | undefined;

export const ASSET_VERSION: string =
  typeof __GRAVELIGHT_ASSET_VERSION__ === 'string' && __GRAVELIGHT_ASSET_VERSION__.length > 0
    ? __GRAVELIGHT_ASSET_VERSION__
    : 'dev';

/**
 * Append the build asset version to a site-relative asset URL.
 * Leaves data:/blob: URLs, external http(s) URLs, protocol-relative URLs, and
 * empty strings untouched; preserves existing query params by appending `&v=`.
 */
export function versionedAssetUrl(path: string): string {
  if (!path) return path;
  if (/^(data:|blob:|https?:|\/\/)/i.test(path)) return path;
  const separator = path.includes('?') ? '&' : '?';
  return `${path}${separator}v=${encodeURIComponent(ASSET_VERSION)}`;
}
